'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { Send, Square } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { Id } from '../../../convex/_generated/dataModel'

interface ComposerProps {
  isStreaming: boolean
  streamingMessageId: Id<'assistantMessages'> | null
  /** One-shot text injection from starter prompts. Consumed on arrival. */
  starterDraft?: string
  onStarterConsumed?: () => void
  onSend: (content: string) => Promise<void>
  onStop: (messageId: Id<'assistantMessages'>) => Promise<void>
}

export function Composer({
  isStreaming,
  streamingMessageId,
  starterDraft = '',
  onStarterConsumed,
  onSend,
  onStop,
}: ComposerProps) {
  const t = useTranslations('assistant')

  // Keystrokes live HERE and only here — the parent never re-renders while
  // the user types (re-rendering the panel would re-render every chart in
  // the message list on each character).
  const [text, setText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Inject starter-prompt text, then immediately hand the draft back to
  // local state (parent resets starterDraft to '').
  useEffect(() => {
    if (starterDraft) {
      setText(starterDraft)
      onStarterConsumed?.()
      textareaRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starterDraft])

  // Auto-resize textarea.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [text])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
  }

  const handleSend = useCallback(async () => {
    const trimmed = text.trim()
    if (!trimmed || isSending || isStreaming) return

    const draft = text
    setText('')
    setIsSending(true)

    try {
      await onSend(trimmed)
    } catch {
      // On failure: restore the draft text so the user doesn't lose their message.
      setText(draft)
    } finally {
      setIsSending(false)
    }
  }, [text, isSending, isStreaming, onSend])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleStop = useCallback(async () => {
    if (streamingMessageId) {
      await onStop(streamingMessageId)
    }
  }, [streamingMessageId, onStop])

  const canSend = !!text.trim() && !isSending && !isStreaming

  return (
    <div
      className="flex-shrink-0 px-3 pt-2.5 pb-2"
      style={{ borderTop: '1px solid var(--color-border)' }}
    >
      {/* Input container with lime focus-within ring */}
      <div
        className="composer-box flex items-end gap-2 rounded-xl px-3 py-2"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '0.75rem',
          transition: 'border-color 150ms, box-shadow 150ms',
        }}
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          // Typing direction follows the content (Hebrew → RTL), not the UI locale.
          dir="auto"
          placeholder={t('composer.placeholder')}
          disabled={isStreaming}
          rows={1}
          className="flex-1 resize-none bg-transparent text-sm outline-none disabled:opacity-50"
          style={{
            minHeight: '24px',
            maxHeight: '120px',
            color: 'var(--color-text-primary)',
          }}
        />

        {isStreaming ? (
          // Stop button
          <button
            type="button"
            onClick={handleStop}
            title={t('composer.stopGenerating')}
            style={{
              flexShrink: 0,
              width: '34px',
              height: '34px',
              borderRadius: '0.6rem',
              border: 'none',
              cursor: 'pointer',
              background: 'var(--color-pink-muted)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 200ms',
            }}
          >
            <Square style={{ width: '14px', height: '14px', color: 'var(--color-pink)' }} />
          </button>
        ) : (
          // Send button: lime background, white icon
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            title={t('composer.sendMessage')}
            style={{
              flexShrink: 0,
              width: '34px',
              height: '34px',
              borderRadius: '0.6rem',
              border: 'none',
              cursor: canSend ? 'pointer' : 'not-allowed',
              background: canSend ? 'var(--color-lime)' : 'var(--color-surface-hover)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 200ms, box-shadow 200ms',
              opacity: canSend ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (canSend) {
                e.currentTarget.style.background = 'var(--color-lime-hover)'
                e.currentTarget.style.boxShadow = 'var(--shadow-glow-lime)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = canSend ? 'var(--color-lime)' : 'var(--color-surface-hover)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <Send
              style={{
                width: '14px',
                height: '14px',
                color: canSend ? '#ffffff' : 'var(--color-text-muted)',
              }}
            />
          </button>
        )}
      </div>

      {/* focus-within lime ring via a global style scoped to this component class */}
      <style jsx global>{`
        .composer-box:focus-within {
          border-color: var(--color-lime) !important;
          box-shadow: 0 0 0 3px var(--color-lime-muted) !important;
        }
      `}</style>
    </div>
  )
}
